import types from '../mutation-types';
import LineItemsAPI from '../../api/lineItems';

export const state = {
  recordsByDeal: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getLineItemsByDeal: _state => dealId => _state.recordsByDeal[dealId] || [],
};

export const actions = {
  index: async ({ commit }, { pipelineId, dealId }) => {
    commit(types.SET_LINE_ITEM_UI_FLAG, { isFetching: true });
    try {
      const { data } = await LineItemsAPI.index(pipelineId, dealId);
      commit(types.SET_DEAL_LINE_ITEMS, {
        dealId,
        items: data.payload || data,
      });
    } catch (error) {
      // Ignore
    } finally {
      commit(types.SET_LINE_ITEM_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit }, { pipelineId, dealId, lineItem }) => {
    commit(types.SET_LINE_ITEM_UI_FLAG, { isCreating: true });
    try {
      const { data } = await LineItemsAPI.createItem(
        pipelineId,
        dealId,
        lineItem
      );
      commit(types.ADD_LINE_ITEM, { dealId, item: data });
      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_LINE_ITEM_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { pipelineId, dealId, id, ...updates }) => {
    commit(types.SET_LINE_ITEM_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await LineItemsAPI.updateItem(
        pipelineId,
        dealId,
        id,
        updates
      );
      commit(types.EDIT_LINE_ITEM, { dealId, item: data });
      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_LINE_ITEM_UI_FLAG, { isUpdating: false });
    }
  },

  bulkUpdate: async ({ commit }, { pipelineId, dealId, lineItems }) => {
    commit(types.SET_LINE_ITEM_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await LineItemsAPI.bulkUpdate(
        pipelineId,
        dealId,
        lineItems
      );
      const items = data.payload || data;
      commit(types.SET_DEAL_LINE_ITEMS, { dealId, items });
      return items;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_LINE_ITEM_UI_FLAG, { isUpdating: false });
    }
  },

  destroy: async ({ commit }, { pipelineId, dealId, id }) => {
    commit(types.SET_LINE_ITEM_UI_FLAG, { isDeleting: true });
    try {
      await LineItemsAPI.destroyItem(pipelineId, dealId, id);
      commit(types.DELETE_LINE_ITEM, { dealId, id });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_LINE_ITEM_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_LINE_ITEM_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.SET_DEAL_LINE_ITEMS](_state, { dealId, items }) {
    _state.recordsByDeal = { ..._state.recordsByDeal, [dealId]: items };
  },
  [types.ADD_LINE_ITEM](_state, { dealId, item }) {
    const existing = _state.recordsByDeal[dealId] || [];
    _state.recordsByDeal = {
      ..._state.recordsByDeal,
      [dealId]: [...existing, item],
    };
  },
  [types.EDIT_LINE_ITEM](_state, { dealId, item }) {
    const existing = _state.recordsByDeal[dealId] || [];
    _state.recordsByDeal = {
      ..._state.recordsByDeal,
      [dealId]: existing.map(li => (li.id === item.id ? item : li)),
    };
  },
  [types.DELETE_LINE_ITEM](_state, { dealId, id }) {
    const existing = _state.recordsByDeal[dealId] || [];
    _state.recordsByDeal = {
      ..._state.recordsByDeal,
      [dealId]: existing.filter(li => li.id !== id),
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
